<app-report-header
  crumbLabel="Cash Flow Calculator"
  (download)="printScreen()"
  (recalculate)="startagain()">
</app-report-header>

<div class="rpt-page" id="contentToConvert">

  <!-- ── Masthead ── -->
  <div class="rpt-wrap rpt-masthead">
    <div class="rpt-masthead__eyebrow">Cash Flow Analysis Report</div>
    <h1 class="rpt-masthead__title">Buy-to-Let Cash Flow — £{{+calcData.PropertyValue | number:'1.0-0'}} over {{investedTenure}} Years</h1>
    <p class="rpt-masthead__subtitle">
      Net cash flow projections for a buy-to-let property, accounting for gross rent, mortgage payments, letting fees, and all running costs.
    </p>
    <div class="rpt-masthead__meta">
      <span class="rpt-meta-pill">Property Value <strong>£{{+calcData.PropertyValue | number:'1.0-0'}}</strong></span>
      <span class="rpt-meta-pill">LTV <strong>{{+calcData.loanAmount}}%</strong></span>
      <span class="rpt-meta-pill">Yield <strong>{{+calcData.rentalYeild | number:'1.2-2'}}%</strong></span>
      <span class="rpt-meta-pill">Period <strong>{{investedTenure}} years</strong></span>
      <span class="rpt-meta-pill rpt-meta-pill--accent">Total Net Cashflow <strong>£{{totalCashFlow | number:'1.0-0'}}</strong></span>
    </div>
  </div>

  <!-- ── §01 KPI Section ── -->
  <section class="rpt-wrap rpt-section">
    <div class="rpt-section__head">
      <div>
        <div class="rpt-section__num">01 · Results</div>
        <h2 class="rpt-section__title">Cash flow summary</h2>
        <p class="rpt-section__sub">Total and Year 1 net cash flow, and initial outlay across the holding period.</p>
      </div>
    </div>

    <div class="rpt-kpi-strip">
      <!-- Hero: Total Net Cashflow -->
      <div class="rpt-kpi-hero">
        <div class="rpt-kpi-hero__eyebrow">Total Net Cashflow</div>
        <div class="rpt-kpi-hero__body">
          <div class="rpt-kpi-hero__value">£{{totalCashFlow | number:'1.0-0'}}</div>
          <div class="rpt-kpi-hero__label">Over {{investedTenure}}-year holding period</div>
        </div>
        <p class="rpt-kpi-hero__caption">
          Net cash flow after all costs — mortgage interest, letting fees, ground rent, service charges, and miscellaneous expenses.
        </p>
      </div>

      <!-- Side: Initial Outlay + Year 1 Cashflow -->
      <div class="rpt-kpi-side rpt-kpi-side--2">
        <div class="rpt-kpi-card">
          <div class="rpt-kpi-card__label">Initial Outlay</div>
          <div class="rpt-kpi-card__value">£{{initialOutlay | number:'1.0-0'}}</div>
          <div class="rpt-kpi-card__foot">Deposit + SDLT + Fees</div>
        </div>
        <div class="rpt-kpi-card">
          <div class="rpt-kpi-card__label">Year 1 Net Cashflow</div>
          <div class="rpt-kpi-card__value">£{{cashflowdata[0]?.CashFlow | number:'1.0-0'}}</div>
          <div class="rpt-kpi-card__foot">First year net return</div>
        </div>
      </div>
    </div>
  </section>

  <!-- ── §02 Assumptions ── -->
  <section class="rpt-wrap rpt-section rpt-section--break-before">
    <div class="rpt-section__head">
      <div>
        <div class="rpt-section__num">02 · Inputs</div>
        <h2 class="rpt-section__title">Your assumptions</h2>
      </div>
    </div>
    <div class="rpt-card">
      <div class="rpt-card__body">
        <div class="rpt-arow">
          <span class="rpt-arow__key">Property Value</span>
          <span class="rpt-arow__val">£{{+calcData.PropertyValue | number:'1.0-0'}}</span>
        </div>
        <div class="rpt-arow">
          <span class="rpt-arow__key">LTV</span>
          <span class="rpt-arow__val">{{+calcData.loanAmount}}%</span>
        </div>
        <div class="rpt-arow">
          <span class="rpt-arow__key">Period</span>
          <span class="rpt-arow__val">{{investedTenure}} years</span>
        </div>
        <div class="rpt-arow">
          <span class="rpt-arow__key">Rental Yield</span>
          <span class="rpt-arow__val">{{+calcData.rentalYeild | number:'1.2-2'}}%</span>
        </div>
        <div class="rpt-arow">
          <span class="rpt-arow__key">Rental Growth</span>
          <span class="rpt-arow__val">{{+calcData.rentalGrowthEscalation | number:'1.2-2'}}% p.a.</span>
        </div>
        <div class="rpt-arow" *ngIf="calcData.optmortgage !== '0'">
          <span class="rpt-arow__key">Mortgage Rate</span>
          <span class="rpt-arow__val">{{+calcData.mortgageInterestRate | number:'1.2-2'}}%</span>
        </div>
        <div class="rpt-arow">
          <span class="rpt-arow__key">Stamp Duty</span>
          <span class="rpt-arow__val">£{{+calcData.stampDutyLandTax | number:'1.0-0'}}</span>
        </div>
        <div class="rpt-arow" *ngIf="calcData.optmortgage !== '0'">
          <span class="rpt-arow__key">Loan Origination Fee</span>
          <span class="rpt-arow__val">£{{+loanoriginationfee | number:'1.0-0'}}</span>
        </div>
        <div class="rpt-arow">
          <span class="rpt-arow__key">Lettings &amp; Management fees</span>
          <span class="rpt-arow__val">{{+calcData.letteingManagFee | number:'1.2-2'}}%</span>
        </div>
        <div class="rpt-arow">
          <span class="rpt-arow__key">Ground Rent</span>
          <span class="rpt-arow__val">£{{+calcData.groundRent | number:'1.0-0'}}</span>
        </div>
        <div class="rpt-arow">
          <span class="rpt-arow__key">Service Charges</span>
          <span class="rpt-arow__val">£{{+calcData.serviceCharges | number:'1.0-0'}}</span>
        </div>
        <div class="rpt-arow">
          <span class="rpt-arow__key">Other Expenses</span>
          <span class="rpt-arow__val">£{{+calcData.miscelleneousExpense | number:'1.0-0'}}</span>
        </div>
      </div>
    </div>
  </section>

  <!-- ── §03 Chart ── -->
  <section class="rpt-wrap rpt-section">
    <div class="rpt-section__head">
      <div>
        <div class="rpt-section__num">03 · Trajectory</div>
        <h2 class="rpt-section__title">Annual net cashflow</h2>
        <p class="rpt-section__sub">Net cash flow by year over the holding period.</p>
      </div>
    </div>
    <div class="rpt-card">
      <div class="rpt-chart-wrap cf-chart-wrap">
        <canvas #cfBarChart></canvas>
      </div>
    </div>
  </section>

  <!-- ── §04 Year-by-Year Table ── -->
  <section class="rpt-wrap rpt-section">
    <div class="rpt-section__head">
      <div>
        <div class="rpt-section__num">04 · Breakdown</div>
        <h2 class="rpt-section__title">Year-by-year breakdown</h2>
      </div>
    </div>
    <div class="rpt-card">
      <div class="rpt-tbl-wrap">
        <table class="rpt-tbl">
          <thead>
            <tr>
              <th>Year</th>
              <th>Gross Rent</th>
              <th>Lettings &amp; Management fees</th>
              <th>Expenses</th>
              <th>Mortgage</th>
              <th>Net Cashflow</th>
              <th>Net Yield</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let row of cashflowdata; let i = index">
              <td style="font-weight:600; color: var(--navy)">{{i + 1}}</td>
              <td>£{{row.GrossRent | number:'1.0-0'}}</td>
              <td style="color: var(--text-70)">-£{{row.LettingFee | number:'1.0-0'}}</td>
              <td style="color: var(--text-70)">-£{{(row.GroundRent + row.ServiceCharges + row.MiscelleneousExpense) | number:'1.0-0'}}</td>
              <td style="color: var(--text-70)">-£{{row.Interest | number:'1.0-0'}}</td>
              <td [style.color]="row.CashFlow >= 0 ? 'var(--green-text)' : '#dc2626'" style="font-weight:600">
                {{row.CashFlow >= 0 ? '£' : '-£'}}{{(row.CashFlow < 0 ? -row.CashFlow : row.CashFlow) | number:'1.0-0'}}
              </td>
              <td>{{netYield[i]}}%</td>
            </tr>
          </tbody>
          <tfoot>
            <tr style="border-top: 2px solid var(--navy); background: var(--surface-2, #f8f9fb)">
              <td colspan="5"></td>
              <td style="line-height:1.4; padding-top:10px; padding-bottom:10px">
                <span style="display:block; font-size:11px; color: var(--text-30, #b0b8c9); font-weight:400; letter-spacing:0.02em">Total Cashflow</span>
                <span style="font-size:17px; font-weight:700; color: var(--green-text)">
                  {{totalCashFlow >= 0 ? '£' : '-£'}}{{(totalCashFlow < 0 ? -totalCashFlow : totalCashFlow) | number:'1.0-0'}}
                </span>
              </td>
              <td style="line-height:1.4; padding-top:10px; padding-bottom:10px">
                <span style="display:block; font-size:11px; color: var(--text-30, #b0b8c9); font-weight:400; letter-spacing:0.02em">Avg Net Yield</span>
                <span style="font-size:17px; font-weight:700; color: var(--green-text)">{{avgnetYield}}%</span>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  </section>

</div>

<!-- Hidden Excel table (preserve for export functionality) -->
<div style="display:none;">
  <table id="cashflowtable">
    <tr class="text-left">
      <th>Set Up Cost:</th>
      <th class="text-center">Year 0</th>
      <th *ngFor="let cashflow of cashflowdata; let i = index" class="text-center">Year {{i + 1}}</th>
    </tr>
    <tbody class="text-center cashflowtablebody">
      <tr>
        <td class="text-left">SDLT</td>
        <td class="text-right">-{{calcData.stampDutyLandTax | currency:'GBP':'symbol':'1.0-0'}}</td>
        <td *ngFor="let cashflow of cashflowdata"></td>
      </tr>
      <tr>
        <td class="text-left">Loan Origination Fee</td>
        <td class="text-right">-{{loanoriginationfee | currency:'GBP':'symbol':'1.0-0'}}</td>
        <td *ngFor="let cashflow of cashflowdata"></td>
      </tr>
      <tr>
        <td class="text-left">Property Value</td>
        <td class="text-right">-{{calcData.PropertyValue | currency:'GBP':'symbol':'1.0-0'}}</td>
        <td *ngFor="let cashflow of cashflowdata" class="text-right"></td>
      </tr>
      <tr>
        <td class="text-left">Gross Rent</td>
        <td></td>
        <td *ngFor="let cashflow of cashflowdata" class="text-right">&#163;{{cashflow.GrossRent | number:'1.0-0'}}</td>
      </tr>
      <tr>
        <td class="text-left">Mortgage interest</td>
        <td></td>
        <td *ngFor="let cashflow of cashflowdata" class="text-right">-{{cashflow.Interest | currency:'GBP':'symbol':'1.0-0'}}</td>
      </tr>
      <tr>
        <td class="text-left">Principal repayment</td>
        <td></td>
        <td *ngFor="let cashflow of cashflowdata" class="text-right">-{{cashflow.PrincipleRepayment | currency:'GBP':'symbol':'1.0-0'}}</td>
      </tr>
      <tr>
        <td class="text-left">Lettings &amp; Management fees</td>
        <td></td>
        <td *ngFor="let cashflow of cashflowdata" class="text-right">-{{cashflow.LettingFee | currency:'GBP':'symbol':'1.0-0'}}</td>
      </tr>
      <tr>
        <td class="text-left">Service Charge</td>
        <td></td>
        <td *ngFor="let cashflow of cashflowdata" class="text-right">-{{cashflow.ServiceCharges | currency:'GBP':'symbol':'1.0-0'}}</td>
      </tr>
      <tr>
        <td class="text-left">Ground rent</td>
        <td></td>
        <td *ngFor="let cashflow of cashflowdata" class="text-right">-{{cashflow.GroundRent | currency:'GBP':'symbol':'1.0-0'}}</td>
      </tr>
      <tr>
        <td class="text-left">Miscellaneous Expenses</td>
        <td></td>
        <td *ngFor="let cashflow of cashflowdata" class="text-right">-{{cashflow.MiscelleneousExpense | currency:'GBP':'symbol':'1.0-0'}}</td>
      </tr>
      <tr>
        <td class="text-left">Loan outstanding</td>
        <td></td>
        <td *ngFor="let cashflow of cashflowdata" class="text-right">{{cashflow.loanOutstanding | currency:'GBP':'symbol':'1.0-0'}}</td>
      </tr>
      <tr style="font-weight:bold;">
        <td class="text-left">Cashflow</td>
        <td class="text-right" *ngFor="let cashflow of allCahsflow">{{cashflow | currency:'GBP':'symbol':'1.0-0'}}</td>
      </tr>
      <tr style="font-weight:bold;">
        <td class="text-left">Net Yield</td>
        <td></td>
        <td class="text-right" *ngFor="let netyield of netYield">{{netyield | number:'1.2'}}%</td>
      </tr>
      <tr style="font-weight:bold;">
        <td class="text-left">Avg Net Yield</td>
        <td class="text-right">{{avgnetYield | number:'1.2'}}%</td>
        <td class="text-right" *ngFor="let netyield of netYield"></td>
      </tr>
      <tr style="font-weight:bold;">
        <td class="text-left">Total Cashflow</td>
        <td class="text-right">{{totalCashFlow | currency:'GBP':'symbol':'1.0-0'}}</td>
        <td class="text-right" *ngFor="let netyield of netYield"></td>
      </tr>
    </tbody>
  </table>
</div>

<app-report-footer
  [today]="today"
  (download)="printScreen()"
  (recalculate)="startagain()">
</app-report-footer>
